/* eslint-disable ember/no-new-mixins, ember/no-runloop, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import Mixin from '@ember/object/mixin';
import { computed } from '@ember/object';
import { service } from '@ember/service';
import { debounce } from '@ember/runloop';
import ProgressionType from '@linc-technologies/linced-common/enums/progression-type';
import Options from '@linc-technologies/linced-common/enums/options';
import { byKeys, findBy } from '@linc-technologies/linced-common/utils/arrays';
import {
  COMPONENT_FIELD_MAP,
  PROGRESS_COMPONENT_TYPES,
  REVIEW_EDITABLE_TYPES,
  parseConfig,
  saveSection,
  manualEditProperties,
} from 'linced-app/utils/reporting';
import {
  resolveRubricElements,
  rubricElementTotals,
  sectionRubricFamilyId,
} from '@linc-technologies/linced-common/utils/rubrics';
import {
  getCurrentMilestone,
  generateExpectationData,
  generateProgressData,
} from '@linc-technologies/linced-common/utils/progressions';

export default Mixin.create({
  // Services
  dates: service(),
  fetch: service(),
  options: service(),
  permissions: service(),
  session: service(),
  store: service(),
  toaster: service(),

  // Review state
  reviewStudentReports: null,
  reviewSelectedStudent: null,
  reviewEditingSection: null,
  reviewEditingComponent: null,
  reviewProgressSubject: null,
  reviewProgressLevels: null,
  reviewProgressExpectations: null,
  reviewProgressRecordsData: null,
  reviewProgressRecordsLoading: false,
  _reviewRefreshFn: null,
  reviewStrandSubject: null,
  reviewStrandProgressComponents: null,
  // The rubric family chosen on the section being reviewed (null for the
  // default family)
  reviewStrandRubricFamilyId: null,
  showRubricDialog: false,
  rubricDialogStudent: null,

  REVIEW_EDITABLE_TYPES,
  REVIEW_COMPONENT_FIELD_MAP: COMPONENT_FIELD_MAP,

  reviewEditableComponentTypes: computed(
    'canEditLeadershipComment',
    function () {
      if (this.canEditLeadershipComment) {
        return REVIEW_EDITABLE_TYPES;
      }
      return REVIEW_EDITABLE_TYPES.filter((t) => t !== 'principal-comment');
    },
  ),

  reviewStudentReportSection: computed(
    'reviewSelectedStudent',
    'reviewEditingSection',
    'reviewStudentReports',
    function () {
      let student = this.reviewSelectedStudent;
      let section = this.reviewEditingSection;
      let reportMap = this.reviewStudentReports;
      if (!student || !section || !reportMap) return null;
      let studentReport = reportMap[student.get('id')];
      if (!studentReport) return null;
      let studentReportId = studentReport.get('id');
      let sectionId = section.get('id');
      return (
        this.store.peekAll('reports/student-report-section').find((s) => {
          let srRef = s.belongsTo('studentReportId');
          let tsRef = s.belongsTo('reportTemplateSectionId');
          return (
            (srRef.value() === studentReport ||
              srRef.id() === studentReportId) &&
            (tsRef.value() === section || tsRef.id() === sectionId)
          );
        }) || null
      );
    },
  ),

  reviewEditingFieldValue: computed(
    'reviewStudentReportSection.{narrative,nextSteps,homeHelp,principalComment}',
    'reviewEditingComponent',
    function () {
      let section = this.reviewStudentReportSection;
      let comp = this.reviewEditingComponent;
      if (!section || !comp) return '';
      let compType = comp.type || (comp.get && comp.get('type'));
      let field = this.REVIEW_COMPONENT_FIELD_MAP[compType];
      if (!field) return '';
      return section.get(field) || '';
    },
  ),

  // The goals half of the next-steps block, edited alongside the rubric /
  // teacher next steps when reviewing a next-steps component.
  reviewNextStepsGoalsValue: computed(
    'reviewStudentReportSection.nextStepsGoals',
    function () {
      let section = this.reviewStudentReportSection;
      return (section && section.get('nextStepsGoals')) || '';
    },
  ),

  reviewProgressRecords: computed(
    'reviewSelectedStudent',
    'reviewProgressSubject.milestones.[]',
    'reviewProgressRecordsData.[]',
    function () {
      let student = this.reviewSelectedStudent;
      let subject = this.reviewProgressSubject;
      let data = this.reviewProgressRecordsData;
      if (!student || !subject || !data) return [];
      let studentId = student.get('id');
      let milestoneIds = new Set(
        (subject.get('milestones') || []).map((m) => m.get('id')),
      );
      return data.filter((p) => {
        let personRef = p.belongsTo('personId');
        let milestoneRef = p.belongsTo('milestoneId');
        return (
          personRef.id() === studentId && milestoneIds.has(milestoneRef.id())
        );
      });
    },
  ),

  reviewProgressCurrentMilestone: computed(
    'reviewSelectedStudent',
    'reviewProgressSubject.milestones.[]',
    function () {
      let student = this.reviewSelectedStudent;
      let subject = this.reviewProgressSubject;
      if (!student || !subject) return null;
      let milestones = subject.get('milestones') || [];
      return getCurrentMilestone(
        milestones,
        student,
        this.dates.rolloverMonthDate,
      );
    },
  ),

  reviewProgressSliderMilestones: computed(
    'reviewProgressCurrentMilestone',
    'reviewProgressSubject.milestones.[]',
    'reviewProgressRecords.[]',
    function () {
      let subject = this.reviewProgressSubject;
      let currentMilestone = this.reviewProgressCurrentMilestone;
      if (!subject || !currentMilestone) return [];
      let milestones = subject.get('milestones') || [];
      let maxIndex = milestones.indexOf(currentMilestone);
      this.reviewProgressRecords.forEach((p) => {
        let milestoneId = p.belongsTo('milestoneId').id();
        let milestone = findBy(milestones, 'id', milestoneId);
        let idx = milestones.indexOf(milestone);
        if (idx > maxIndex) maxIndex = idx;
      });
      return milestones.slice(0, maxIndex + 1).reverse();
    },
  ),

  reviewProgressChartLineData: computed(
    'reviewProgressSubject.milestones.[]',
    'reviewProgressLevels.[]',
    'reviewProgressRecords.[]',
    function () {
      let subject = this.reviewProgressSubject;
      let levels = this.reviewProgressLevels;
      if (!subject || !levels) return null;
      let milestones = subject.get('milestones') || [];
      return generateProgressData(
        milestones,
        levels,
        this.reviewProgressRecords,
      );
    },
  ),

  _updateReviewStrand(field, elementId, value) {
    let student = this.reviewSelectedStudent;
    let section = this.reviewStudentReportSection;
    let parent = this.parentController;
    let isJudgement = field === 'judgement';
    let save = (elId, v) =>
      parent.saveReportProgression(
        student,
        {
          judgement: isJudgement ? v : null,
          effort: isJudgement ? null : v,
          elementId: elId,
        },
        this.reviewStrandSubject,
      );

    if (section) {
      if (!elementId) {
        section.set(field, String(value));
        section.set('complete', false);
        save(null, value);
      } else {
        let elementProgressions = section.get('elementProgressions');
        let currentElements = resolveRubricElements(
          this.reviewStrandSubject,
          student,
          this.store,
          this.dates.rolloverMonthDate,
          this.reviewStrandRubricFamilyId,
        );
        let hasAnyValue =
          section.get(field) || elementProgressions.any((ep) => ep.get(field));

        if (!hasAnyValue) {
          let subject = this.reviewStrandSubject;
          let milestoneIds = new Set(
            (subject ? subject.get('milestones') || [] : []).map((m) =>
              m.get('id'),
            ),
          );
          let studentId = student.get('id');
          hasAnyValue = this.store
            .peekAll('milestones/progression')
            .some(
              (p) =>
                p.belongsTo('personId').id() === studentId &&
                milestoneIds.has(p.belongsTo('milestoneId').id()) &&
                p.get(field),
            );
        }

        if (!hasAnyValue) {
          // Keep null as null (not the string "null") for the same reason as
          // the else branch below — otherwise a re-enable here persists "null"
          // on every element, which later poisons the average via parseInt.
          let stored = value == null ? null : String(value);
          currentElements.forEach((el) => {
            let elId = el.get('id');
            let ep = findBy(elementProgressions, 'elementId', elId);
            if (ep) {
              ep.set(field, stored);
            } else {
              elementProgressions.createFragment({
                elementId: elId,
                [field]: stored,
              });
            }
            save(elId, value);
          });
          section.set(field, stored);
          save(null, value);
        } else {
          // Re-enabling a strand emits null; keep it as null rather than the
          // string "null" so the averaging loop's `v != null` guard skips it.
          // Otherwise parseInt("null") === NaN poisons the average and wipes
          // the overall grade.
          let stored = value == null ? null : String(value);
          let existing = findBy(elementProgressions, 'elementId', elementId);
          if (existing) {
            existing.set(field, stored);
          } else {
            elementProgressions.createFragment({
              elementId,
              [field]: stored,
            });
          }

          // Only the current rubric's strands count; values stored against
          // another rubric's elements (e.g. before the template's rubric
          // family changed) are ignored
          let { total, count } = rubricElementTotals(
            elementProgressions,
            currentElements,
            field,
          );
          if (count > 0) {
            let avg = Math.round(total / count);
            section.set(field, String(avg));
            save(null, avg);
          }
          save(elementId, value);
        }

        section.set('complete', false);
      }

      saveSection(section, this.toaster);
    }
  },

  _cleanupReviewState() {
    this.setProperties({
      reviewStudentReports: null,
      reviewSelectedStudent: null,
      reviewEditingSection: null,
      reviewEditingComponent: null,
      reviewProgressSubject: null,
      reviewProgressLevels: null,
      reviewProgressExpectations: null,
      reviewProgressRecordsData: null,
      reviewProgressRecordsLoading: false,
      reviewStrandSubject: null,
      reviewStrandProgressComponents: null,
      reviewStrandRubricFamilyId: null,
      showRubricDialog: false,
      rubricDialogStudent: null,
    });
  },

  _selectReviewComponent(section, component) {
    let compType = component.type || (component.get && component.get('type'));
    if (!this.REVIEW_EDITABLE_TYPES.includes(compType)) return;
    if (compType === 'principal-comment' && !this.canEditLeadershipComment)
      return;
    this.set('reviewEditingSection', section);
    this.set('reviewEditingComponent', component);

    if (compType === 'progress-visual') {
      let config = section.get('configuration');
      let parsed = parseConfig(config);
      let subjectId = parsed && parsed.subjectId;
      if (!subjectId) return;

      let subject = this.store.peekRecord('goals/subject', subjectId);
      let milestones = (subject && subject.get('milestones')) || [];
      let levels = this.store
        .peekAll('milestones/level')
        .filter((l) => l.belongsTo('goalSubjectId').id() === subjectId)
        .sort(byKeys('sequence', 'label'));
      let expectations = this.store
        .peekAll('milestones/expectation')
        .filter((e) => e.belongsTo('goalSubjectId').id() === subjectId);
      let showAllColours = this.options.schoolGet(
        this.session.school,
        Options.CHARTS_SHOW_ALL_COLOURS,
      );
      let expectationData = generateExpectationData(
        expectations,
        milestones,
        levels,
        showAllColours,
      );

      this.set('reviewProgressSubject', subject);
      this.set('reviewProgressLevels', levels);
      this.set('reviewProgressExpectations', expectationData);
      this.set('reviewProgressRecordsData', null);
      this.set('reviewProgressRecordsLoading', true);

      let student = this.reviewSelectedStudent;
      this.store
        .query('milestones/progression', {
          personId: student.get('id'),
          goalSubjectId: subjectId,
          latest: true,
          type: ProgressionType.PROGRESSION,
        })
        .then((records) => {
          this.set('reviewProgressRecordsData', records);
          this.set('reviewProgressRecordsLoading', false);
        })
        .catch(() => {
          this.set('reviewProgressRecordsLoading', false);
        });
    } else if (
      compType === 'strand-visual' ||
      compType === 'effort' ||
      compType === 'progress-descriptor'
    ) {
      let config = section.get('configuration');
      let parsed = parseConfig(config);
      let subjectId = parsed && parsed.subjectId;
      if (!subjectId) return;

      let subject = this.store.peekRecord('goals/subject', subjectId);
      let components = (parsed.components || []).filter((c) =>
        PROGRESS_COMPONENT_TYPES.includes(c.type),
      );
      this.setProperties({
        reviewStrandSubject: subject,
        reviewStrandProgressComponents: components,
        reviewStrandRubricFamilyId: sectionRubricFamilyId(section),
      });
    }
  },

  _closeReviewEditor() {
    this.setProperties({
      reviewEditingSection: null,
      reviewEditingComponent: null,
      reviewProgressSubject: null,
      reviewProgressLevels: null,
      reviewProgressExpectations: null,
      reviewProgressRecordsData: null,
      reviewProgressRecordsLoading: false,
      reviewStrandSubject: null,
      reviewStrandProgressComponents: null,
      reviewStrandRubricFamilyId: null,
    });
    if (this._reviewRefreshFn) {
      this._reviewRefreshFn();
    }
  },

  // componentType overrides the editing component's type, for the second
  // (goals) editor shown for next steps.
  _updateReviewField(value, componentType) {
    let section = this.reviewStudentReportSection;
    let comp = this.reviewEditingComponent;
    if (!section || !comp) return;
    let compType = componentType || comp.type || (comp.get && comp.get('type'));
    let field = this.REVIEW_COMPONENT_FIELD_MAP[compType];
    if (!field) return;
    // A manual edit stops slider changes regenerating this field. This
    // editor also reports TinyMCE's load-time normalisation, so only an
    // actual change counts.
    if (value !== section.get(field)) {
      section.setProperties(manualEditProperties(field, value));
    }
    if (compType === 'principal-comment') {
      if (!value) {
        section.set('complete', false);
      } else if (this.canEditLeadershipComment) {
        section.set('complete', true);
      }
    }
    debounce(section, saveSection, section, this.toaster, 1000);
  },
});
